import { AtSign, Github, Linkedin, Mail, MessageCircle, Send, User } from "lucide-react";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

const Contact = () => {
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const recaptchaRef = useRef();

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "aadarsha871@gmail.com",
      href: "https://mail.google.com/mail/?view=cm&fs=1&to=achaulagain444@gmail.com",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: "linkedin.com/in/aadarsha-chaulagain",
      href: "https://www.linkedin.com/in/aadarsha-chaulagain-648a002b9",
      color: "from-blue-600 to-blue-500",
    },
    {
      icon: Github,
      label: "GitHub",
      value: "github.com/aadarsha875",
      href: "https://github.com/aadarsha875",
      color: "from-gray-700 to-gray-600",
    },
  ];

  // Show captcha on first click
  const handleSubmitClick = (e) => {
    if (!showCaptcha) {
      e.preventDefault();
      setShowCaptcha(true);
    }
  };

  // Form submit handler (AJAX to FormSubmit)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (recaptchaRef.current) {
      const captchaValue = recaptchaRef.current.getValue();
      if (!captchaValue) {
        alert("Please verify the captcha before submitting!");
        return;
      }
    }

    setIsSubmitting(true);
    const form = e.target;
    const formData = new FormData(form);

    try {
      const res = await fetch(
        "https://formsubmit.co/ajax/9daadf95f19d74836f94790f3b1e3d75",
        {
          method: "POST",
          body: formData,
        }
      );

      if (res.ok) {
        form.reset();
        setShowCaptcha(false);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 4000); // toast disappears after 4 seconds
      } else {
        console.error("FormSubmit error:", res.statusText);
        alert("Failed to send message. Try again.");
      }
    } catch (err) {
      console.error("Submission failed:", err);
      alert("Error sending message. Try again.");
    }

    setIsSubmitting(false);
  };

  return (
    <section className="min-h-[calc(100vh-80px)] px-6 sm:px-8 py-20 mx-auto max-w-7xl overflow-hidden">

      {/* Header */}
      <div className="text-center mb-16">
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Get In Touch
          </span>
        </h2>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Have a project in mind or just want to chat? I'd love to hear from you. Let's create something amazing together.
        </p>
        <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-8 rounded-full"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact Info */}
        <div className="space-y-8">
          <div className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <MessageCircle className="text-blue-500" size={28} />
              Let's Connect
            </h3>
            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <a
                    key={index}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    <div
                      className={`p-3 rounded-xl bg-gradient-to-r ${item.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                        {item.label}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{item.value}</div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <Send className="text-purple-500" size={28} />
            Send Message
          </h3>

          <form id="contactForm" onSubmit={handleFormSubmit}>
            <input type="hidden" name="_template" value="table" />
            <input type="hidden" name="_subject" value="New Portfolio Message" />
            <input type="text" name="_honey" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

            {/* Name */}
            <div className="relative mb-4">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                name="name"
                required
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                placeholder="Enter your name"
              />
            </div>

            {/* Email */}
            <div className="relative mb-4">
              <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="email"
                name="email"
                required
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                placeholder="you@example.com"
              />
            </div>

            {/* Message */}
            <div className="relative mb-4">
              <MessageCircle className="absolute left-3 top-4 text-gray-400" size={20} />
              <textarea
                name="message"
                rows="5"
                required
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 resize-none"
                placeholder="Write your message here..."
              />
            </div>

            {/* Captcha */}
            {showCaptcha && (
              <div className="flex justify-center mb-4">
                <ReCAPTCHA sitekey="6Lce3b4rAAAAAJxoY5BWZ89QNAvjs8YWS4jirVEs" ref={recaptchaRef} />
              </div>
            )}

            {/* Button */}
            <button
              type="submit"
              onClick={handleSubmitClick}
              disabled={isSubmitting}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg flex items-center justify-center gap-2"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>

      {/* Toast Notification */}
      {toastVisible && (
        <div className="fixed bottom-8 right-8 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg animate-fadeInOut">
          Message sent successfully!
        </div>
      )}

      {/* Toast animation */}
      <style>
        {`
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(20px); }
            10% { opacity: 1; transform: translateY(0); }
            90% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(20px); }
          }
          .animate-fadeInOut {
            animation: fadeInOut 4s ease forwards;
          }
        `}
      </style>
    </section>
  );
};

export default Contact;